import type { CSSProperties, ReactNode } from 'react'

interface PageHeaderProps {
  title: string
  description?: ReactNode
  actions?: ReactNode
  eyebrow?: string
}

export function PageHeader({ title, description, actions, eyebrow }: PageHeaderProps) {
  return (
    <section className="page-header">
      <div className="page-header__copy">
        {eyebrow && <p className="page-header__eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <div className="page-header__description">{description}</div>}
      </div>
      {actions && <div className="page-header__actions">{actions}</div>}
    </section>
  )
}

interface NoticeProps {
  children: ReactNode
  variant?: 'info' | 'success' | 'warning' | 'error'
  testid?: string
  role?: string
  actions?: ReactNode
}

export function Notice({
  children,
  variant = 'info',
  testid,
  role,
  actions,
}: NoticeProps) {
  return (
    <div className={`notice notice--${variant}`} data-testid={testid} role={role}>
      <div className="notice__content">{children}</div>
      {actions && <div className="notice__actions">{actions}</div>}
    </div>
  )
}

interface SurfaceProps {
  children: ReactNode
  className?: string
  testid?: string
  as?: 'section' | 'div' | 'main' | 'aside'
  style?: CSSProperties
}

export function Surface({ children, className = '', testid, as = 'section', style }: SurfaceProps) {
  const Component = as
  return (
    <Component className={`surface ${className}`.trim()} data-testid={testid} style={style}>
      {children}
    </Component>
  )
}

interface IconButtonProps {
  label: string
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  testid?: string
  title?: string
  className?: string
  type?: 'button' | 'submit'
}

export function IconButton({
  label,
  children,
  onClick,
  disabled,
  testid,
  title,
  className = '',
  type = 'button',
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={title ?? label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`icon-button ${className}`.trim()}
    >
      {children}
    </button>
  )
}
